import { useMemo, useState } from "react";
import AssessmentHelpPanel from "./AssessmentHelpPanel";
import AssessmentSharedNav from "./AssessmentSharedNav";
import {
  AssessmentPillarStatus,
  AssessmentQuestionNav,
  AssessmentRatingGuide,
} from "./AssessmentChromeBars";
import { assessmentChromeTokens } from "./assessmentChromeTokens";
import { V1StyleQuestionBlocks } from "./V1StyleAssessmentLayout";
import { useTheme } from "../context/ThemeContext";
import {
  ASSESSMENT_UX_FORM,
  ASSESSMENT_UX_GUIDE,
} from "../utils/assessmentUxMode";
import { buildHelpPillar } from "../utils/helpContext";
import {
  findFirstUnansweredQuestionId,
  findLastAnsweredQuestionId,
  isQuestionAnswered,
} from "../utils/questionProgress";
import { trackLabel, productName, isFiveXType } from "../utils/participantAssessmentType";

/**
 * Survey form mode — same chrome (nav / rating guide / pillars / q-nav) as Conversational.
 */
export default function SurveyFormAssessment({
  steps = [],
  currentStep = 1,
  setCurrentStep,
  answers,
  setAnswerField,
  companyName,
  trackBanner,
  singlePageMode = false,
  questionsOverride = null,
  saving = false,
  onComplete,
  onNextStep,
  onBackStep,
  uxMode = ASSESSMENT_UX_FORM,
  onUxModeChange,
  surveyToken,
  assessmentTrack = "lite",
  assessmentType,
  productLogoUrl = null,
  productDisplayName = null,
  primaryColor = null,
}) {
  const { theme, toggleTheme } = useTheme();
  const dark = theme === "dark";
  const T = assessmentChromeTokens(dark, primaryColor);

  const stepData = steps[currentStep - 1];
  const isLast = currentStep >= steps.length;
  const [focusedQuestionId, setFocusedQuestionId] = useState(null);
  const [askRequest, setAskRequest] = useState(null);
  const [activityKey, setActivityKey] = useState(0);

  const orderedQuestions = useMemo(() => {
    if (singlePageMode) {
      return (steps || []).flatMap((s) => s.questions || []);
    }
    if (questionsOverride?.length) return questionsOverride;
    return stepData?.questions || [];
  }, [singlePageMode, steps, questionsOverride, stepData]);

  const answeredCount = useMemo(
    () => orderedQuestions.filter((q) => isQuestionAnswered(q, answers)).length,
    [orderedQuestions, answers]
  );
  const lastAnsweredQuestionId = findLastAnsweredQuestionId(
    orderedQuestions,
    answers
  );
  const firstUnansweredQuestionId = findFirstUnansweredQuestionId(
    orderedQuestions,
    answers
  );
  const allAnswered =
    orderedQuestions.length > 0 && answeredCount === orderedQuestions.length;

  const activePillarStep = useMemo(() => {
    if (singlePageMode) {
      return (
        steps.find((s) =>
          (s.questions || []).some(
            (q) =>
              String(q.id) ===
              String(focusedQuestionId || firstUnansweredQuestionId)
          )
        )?.step ?? steps[0]?.step
      );
    }
    return stepData?.step;
  }, [
    singlePageMode,
    steps,
    stepData?.step,
    focusedQuestionId,
    firstUnansweredQuestionId,
  ]);

  const navQuestions = useMemo(() => {
    const pillar = (steps || []).find(
      (s) => Number(s.step) === Number(activePillarStep)
    );
    if (pillar?.questions?.length) return pillar.questions;
    if (!singlePageMode && orderedQuestions.length) return orderedQuestions;
    return steps[0]?.questions || [];
  }, [steps, activePillarStep, singlePageMode, orderedQuestions]);

  const progressQuestions = singlePageMode
    ? orderedQuestions
    : (steps || []).flatMap((s) => s.questions || []);
  const progressAnswered = useMemo(
    () => progressQuestions.filter((q) => isQuestionAnswered(q, answers)).length,
    [progressQuestions, answers]
  );
  const progressPct = progressQuestions.length
    ? Math.round((progressAnswered / progressQuestions.length) * 100)
    : 0;

  const helpPillars = useMemo(() => {
    if (singlePageMode) {
      return (steps || [])
        .map((s) =>
          buildHelpPillar({
            step: s.step,
            title: s.title,
            questions: s.questions || [],
          })
        )
        .filter((p) => p.questions.length);
    }
    return [
      buildHelpPillar({
        step: stepData?.step,
        title: stepData?.title,
        questions: orderedQuestions,
      }),
    ].filter((p) => p.questions.length);
  }, [singlePageMode, steps, stepData, orderedQuestions]);

  const bumpActivity = () => setActivityKey((k) => k + 1);

  const setAnswerFieldTracked = (question, part, value) => {
    bumpActivity();
    setAnswerField?.(question, part, value);
  };

  const openAskAbout = (question) => {
    const qid = question?.id != null ? String(question.id) : "";
    const qtext = String(question?.text || "").trim();
    if (qid) setFocusedQuestionId(qid);
    const text = qid
      ? `Explain question ${qid}: ${qtext}`
      : qtext
        ? `Explain this statement: ${qtext}`
        : "Explain this statement";
    setAskRequest({
      text,
      nonce: Date.now(),
    });
  };

  const jumpToQuestion = (q) => {
    if (!q?.id) return;
    setFocusedQuestionId(String(q.id));
    if (!singlePageMode) {
      const stepIdx = (steps || []).findIndex((s) =>
        (s.questions || []).some((qq) => String(qq.id) === String(q.id))
      );
      if (stepIdx >= 0 && stepIdx + 1 !== currentStep) {
        setCurrentStep?.(stepIdx + 1);
      }
    }
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-question-id="${q.id}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const sectionTitle = singlePageMode
    ? trackBanner || trackLabel(assessmentType, "lite")
    : stepData?.title || `Section ${currentStep}`;

  return (
    <>
      <style>{`
        @keyframes lga-pulse-dot { 0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(.75)} }
        @keyframes lga-progress  { 0%{background-position:-200% center}100%{background-position:200% center} }
        @keyframes lga-fadein    { from{opacity:0}to{opacity:1} }
        .sfa-form-shell {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          position: relative;
          z-index: 10;
        }
        .sfa-form-inner {
          max-width: 960px;
          margin: 0 auto;
          padding: 18px 16px 120px;
        }
        .sfa-form-card {
          border-radius: 20px;
          padding: 8px 4px 16px;
          background: ${T.contentCard};
          border: 1px solid ${T.contentBorder};
          box-shadow: ${
            dark
              ? "0 18px 50px rgba(0,0,0,0.28)"
              : "0 12px 40px rgba(0,79,115,0.08)"
          };
        }
        .sfa-form-heading {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          padding: 12px 16px 8px;
        }
        .sfa-form-title {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: ${T.text1};
        }
        .sfa-form-sub {
          margin: 4px 0 0;
          font-size: 13px;
          line-height: 1.55;
          color: ${T.text2};
          max-width: 40rem;
        }
        .sfa-form-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .sfa-form-chip {
          font-size: 11px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 999px;
          color: ${T.text2};
          background: ${T.chipBg};
          border: 1px solid ${T.chipBorder};
        }
        .sfa-form-footer {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          gap: 12px;
          margin-top: 18px;
        }
        .sfa-form-btn {
          border-radius: 999px;
          padding: 11px 20px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.15s ease, opacity 0.15s ease;
        }
        .sfa-form-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .sfa-form-btn:not(:disabled):hover { transform: translateY(-1px); }
        .sfa-form-btn-primary {
          border: none;
          color: ${T.primaryBtnText};
          background: ${T.primaryBtn};
          box-shadow: 0 8px 24px rgba(0,79,115,0.28);
        }
        .sfa-form-btn-ghost {
          color: ${T.ghostBtnText};
          background: transparent;
          border: 1px solid ${T.ghostBtnBorder};
        }
        .sfa-form-hint {
          text-align: center;
          margin-top: 16px;
          font-size: 12px;
          color: ${T.text3};
        }
        .sfa-form-hint button {
          color: ${T.brand};
          font-weight: 700;
          text-decoration: underline;
          text-underline-offset: 3px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
        }
        ${
          dark
            ? `
        .sfa-form-card { color: #e2e8f0; }
        .sfa-form-card .text-gray-900 { color: #f1f8fb !important; }
        .sfa-form-card .text-gray-700,
        .sfa-form-card .text-gray-600 { color: rgba(232,244,248,0.72) !important; }
        .sfa-form-card .bg-white {
          background: rgba(8,15,28,0.92) !important;
          border-color: rgba(148,163,184,0.18) !important;
          box-shadow: none !important;
        }
        .sfa-form-card [style*="border-left"] {
          border-left-color: rgba(103,232,249,0.55) !important;
        }
        .sfa-form-card .ring-\\[\\#004F73\\]\\/25 {
          --tw-ring-color: rgba(103,232,249,0.35) !important;
        }
        .sfa-form-card .bg-white\\/80 {
          background: rgba(255,255,255,0.08) !important;
        }
        .sfa-form-card .sfa-ask-ai-btn,
        .sfa-form-card button.border-\\[\\#004F73\\]\\/25 {
          color: #67e8f9 !important;
          border-color: rgba(103,232,249,0.35) !important;
          background: rgba(56,189,248,0.12) !important;
        }
        .sfa-form-card .mcq-chip-default {
          background: rgba(15,23,42,0.85) !important;
          border-color: rgba(148,163,184,0.35) !important;
          color: rgba(241,248,251,0.92) !important;
        }
        .sfa-form-card .mcq-chip-selected {
          background: #0b6a8f !important;
          border-color: #67e8f9 !important;
          color: #fff !important;
        }
        .sfa-form-card .mcq-part-label { color: rgba(148,163,184,0.95) !important; }
        .sfa-form-card .mcq-part-value { color: #67e8f9 !important; }
        `
            : ""
        }
      `}</style>

      <div
        className="lga-app"
        style={{
          display: "flex",
          flexDirection: "column",
          background: T.page,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage: T.pageDots,
            backgroundSize: "28px 28px",
          }}
        />

        <AssessmentSharedNav
          T={T}
          companyName={companyName}
          productName={
            isFiveXType(assessmentType)
              ? null
              : productDisplayName || productName(assessmentType)
          }
          productLogoUrl={productLogoUrl}
          modeBadge="Survey form"
          uxMode={uxMode}
          onUxModeChange={onUxModeChange}
          showProgress
          progressPct={progressPct}
          questionsLeft={progressQuestions.length - progressAnswered}
          onToggleTheme={toggleTheme}
          useDefaultProductLogo={isFiveXType(assessmentType)}
        />

        <AssessmentRatingGuide T={T} assessmentType={assessmentType} />

        <AssessmentPillarStatus
          T={T}
          steps={steps}
          answers={answers}
          activeStep={activePillarStep}
        />

        <AssessmentQuestionNav
          T={T}
          questions={navQuestions}
          answers={answers}
          activeQuestionId={focusedQuestionId || firstUnansweredQuestionId}
          onSelectQuestion={jumpToQuestion}
        />

        <div className="sfa-form-shell">
          <div className="sfa-form-inner">
            <div className="sfa-form-card">
              <div className="sfa-form-heading">
                <div>
                  <h1 className="sfa-form-title">{sectionTitle}</h1>
                  <p className="sfa-form-sub">
                    {trackBanner ||
                      "Rate importance and agreement for each statement. Use Ask AI about this anytime."}
                  </p>
                </div>
                <div className="sfa-form-meta">
                  <span className="sfa-form-chip">
                    {answeredCount}/{orderedQuestions.length} answered
                  </span>
                  {!singlePageMode ? (
                    <span className="sfa-form-chip">
                      Section {currentStep}/{steps.length}
                    </span>
                  ) : null}
                </div>
              </div>

              {singlePageMode ? (
                steps.map((step) => (
                  <section key={step.step ?? step.title}>
                    <p
                      style={{
                        margin: "10px 16px 4px",
                        fontSize: 15,
                        fontWeight: 800,
                        color: T.brand,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {step.title || `Section ${step.step}`}
                    </p>
                    <V1StyleQuestionBlocks
                      stepData={step}
                      answers={answers}
                      setAnswerField={setAnswerFieldTracked}
                      onAskQuestion={openAskAbout}
                      onFocusedQuestionChange={setFocusedQuestionId}
                      focusMode="viewport-line"
                      activeQuestionId={focusedQuestionId}
                      assessmentType={assessmentType}
                    />
                  </section>
                ))
              ) : (
                <V1StyleQuestionBlocks
                  stepData={stepData}
                  questions={questionsOverride}
                  answers={answers}
                  setAnswerField={setAnswerFieldTracked}
                  onAskQuestion={openAskAbout}
                  onFocusedQuestionChange={setFocusedQuestionId}
                  focusMode="viewport-line"
                  activeQuestionId={focusedQuestionId}
                  assessmentType={assessmentType}
                />
              )}
            </div>

            <div className="sfa-form-footer">
              {!singlePageMode ? (
                <button
                  type="button"
                  className="sfa-form-btn sfa-form-btn-ghost"
                  disabled={currentStep <= 1 || saving}
                  onClick={() => {
                    if (onBackStep) onBackStep();
                    else setCurrentStep?.((s) => Math.max(1, s - 1));
                  }}
                >
                  Back
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                className="sfa-form-btn sfa-form-btn-primary"
                disabled={saving}
                onClick={() => {
                  if (singlePageMode || isLast) onComplete?.();
                  else if (onNextStep) onNextStep();
                  else setCurrentStep?.((s) => Math.min(steps.length, s + 1));
                }}
              >
                {saving
                  ? "Saving…"
                  : singlePageMode || isLast
                    ? "Submit assessment"
                    : "Next section"}
              </button>
            </div>

            <p className="sfa-form-hint">
              Prefer one question at a time with the AI coach? Switch to{" "}
              <button
                type="button"
                onClick={() => onUxModeChange?.(ASSESSMENT_UX_GUIDE)}
              >
                Conversational
              </button>
              .
            </p>
          </div>
        </div>
      </div>

      {surveyToken ? (
        <AssessmentHelpPanel
          surveyToken={surveyToken}
          step={stepData?.step ?? steps[0]?.step}
          sectionTitle={sectionTitle}
          pillars={helpPillars}
          questions={orderedQuestions}
          focusedPillarStep={
            singlePageMode
              ? steps.find((s) =>
                  (s.questions || []).some(
                    (q) => String(q.id) === String(focusedQuestionId)
                  )
                )?.step
              : stepData?.step
          }
          focusedQuestionId={focusedQuestionId}
          lastAnsweredQuestionId={lastAnsweredQuestionId}
          firstUnansweredQuestionId={firstUnansweredQuestionId}
          assessmentTrack={assessmentTrack}
          activityKey={activityKey}
          allAnswered={allAnswered}
          externalQuestionRequest={askRequest}
          assessmentType={assessmentType}
        />
      ) : null}
    </>
  );
}
